import { ScheduleChangeRequest } from "@/hooks/schedules/useScheduleChangeRequests";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, X, Clock, CalendarClock } from "lucide-react";
import { format, isPast, isToday } from "date-fns";
import { es } from "date-fns/locale";

const DAY_NAMES_SHORT = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

const STATUS_CONFIG = {
  pending: { label: "Pendiente", variant: "secondary" as const, icon: Clock, className: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" },
  approved: { label: "Aprobado", variant: "default" as const, icon: Check, className: "bg-green-500/10 text-green-600 border-green-500/20" },
  rejected: { label: "Rechazado", variant: "destructive" as const, icon: X, className: "bg-destructive/10 text-destructive border-destructive/20" },
};

const parseLocalDate = (dateStr: string) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
};

interface Props {
  requests: ScheduleChangeRequest[];
  mode: "member" | "professor";
  onResolve?: (id: string, status: "approved" | "rejected") => void;
}

export const ChangeRequestList = ({ requests, mode, onResolve }: Props) => {
  if (requests.length === 0) return null;

  return (
    <div className="space-y-2">
      {requests.map(req => {
        const statusConfig = STATUS_CONFIG[req.status];
        const StatusIcon = statusConfig.icon;

        const hasDateInfo = req.original_date && req.replacement_date;
        const originalDate = req.original_date ? parseLocalDate(req.original_date) : null;
        const replacementDate = req.replacement_date ? parseLocalDate(req.replacement_date) : null;
        const isExpired = replacementDate ? isPast(replacementDate) && !isToday(replacementDate) : false;
        const isActiveToday = replacementDate ? isToday(replacementDate) : false;

        return (
          <Card key={req.id} className={`overflow-hidden ${isExpired ? "opacity-60" : ""}`}>
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  {mode === "professor" && req.requester && (
                    <p className="font-medium text-foreground mb-1">
                      {req.requester.first_name} {req.requester.last_name}
                    </p>
                  )}
                  {hasDateInfo ? (
                    <p className="text-muted-foreground">
                      <span className="line-through">
                        {DAY_NAMES_SHORT[req.current_day]} {format(originalDate!, "d/M", { locale: es })} {req.current_start_time.slice(0, 5)}
                      </span>
                      {" → "}
                      <span className="font-medium text-foreground">
                        {DAY_NAMES_SHORT[req.requested_day]} {format(replacementDate!, "d/M", { locale: es })} {req.requested_start_time.slice(0, 5)} - {req.requested_end_time.slice(0, 5)}
                      </span>
                    </p>
                  ) : (
                    <p className="text-muted-foreground">
                      <span className="line-through">{DAY_NAMES_SHORT[req.current_day]} {req.current_start_time.slice(0, 5)}</span>
                      {" → "}
                      <span className="font-medium text-foreground">
                        {DAY_NAMES_SHORT[req.requested_day]} {req.requested_start_time.slice(0, 5)} - {req.requested_end_time.slice(0, 5)}
                      </span>
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge className={statusConfig.className}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {statusConfig.label}
                  </Badge>
                  {hasDateInfo && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1">
                      <CalendarClock className="w-3 h-3" />
                      {isActiveToday ? "Hoy" : isExpired ? "Pasado" : "Solo esta semana"}
                    </Badge>
                  )}
                </div>
              </div>

              {req.reason && (
                <p className="text-xs text-muted-foreground italic">"{req.reason}"</p>
              )}

              <p className="text-xs text-muted-foreground">
                {format(new Date(req.created_at), "d MMM yyyy", { locale: es })}
              </p>

              {mode === "professor" && req.status === "pending" && onResolve && (
                <div className="flex gap-2 pt-1">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => onResolve(req.id, "approved")}>
                    <Check className="w-3 h-3 mr-1" /> Aprobar
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1 text-destructive hover:text-destructive" onClick={() => onResolve(req.id, "rejected")}>
                    <X className="w-3 h-3 mr-1" /> Rechazar
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
